/**
 * Futures (COIN-M) REST API
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    BasisResponse,
    CheckServerTimeResponse,
    CompressedAggregateTradesListResponse,
    ContinuousContractKlineCandlestickDataResponse,
    ExchangeInformationResponse,
    GetFundingRateHistoryOfPerpetualFuturesResponse,
    GetFundingRateInfoResponse,
    IndexPriceAndMarkPriceResponse,
    IndexPriceKlineCandlestickDataResponse,
    KlineCandlestickDataResponse,
    LongShortRatioResponse,
    MarkPriceKlineCandlestickDataResponse,
    OldTradesLookupResponse,
    OpenInterestResponse,
    OpenInterestStatisticsResponse,
    OrderBookResponse,
    PremiumIndexKlineDataResponse,
    QueryIndexPriceConstituentsResponse,
    RecentTradesListResponse,
    SymbolOrderBookTickerResponse,
    SymbolPriceTickerResponse,
    TakerBuySellVolumeResponse,
    Ticker24hrPriceChangeStatisticsResponse,
    TopTraderLongShortRatioAccountsResponse,
    TopTraderLongShortRatioPositionsResponse,
} from '../types';

/**
 * MarketDataApi - axios parameter creator
 */
const MarketDataApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Query basis
         *
         * Weight(IP): 1
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Basis
         * @param {string} pair Pair.
         * @param {BasisContractTypeEnum} contractType Contract type.
         * @param {BasisPeriodEnum} period Period interval.
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        basis: async (
            pair: string,
            contractType: BasisContractTypeEnum,
            period: BasisPeriodEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('basis', 'pair', pair);
            // verify required parameter 'contractType' is not null or undefined
            assertParamExists('basis', 'contractType', contractType);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('basis', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/basis',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Test connectivity to the Rest API and get the current server time.
         *
         * Weight(IP): 1
         *
         * @summary Check Server time
         *
         * @throws {RequiredError}
         */
        checkServerTime: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/time',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get compressed, aggregate trades. Market trades that fill in 100ms with
         * the same price and the same taking side will have the quantity
         * aggregated.
         *
         * Weight(IP): 20
         *
         * Notes:
         * - only trade histories within the past 48 hours (counted from now) can be queried
         * - If both `startTime` and `endTime` are sent, time between `startTime` and `endTime` must be less than 1 hour.
         * - If `fromId`, `startTime`, and `endTime` are not sent, the most recent aggregate trades will be returned.
         * - Only market trades will be aggregated and returned, which means the insurance fund trades and ADL trades won't be aggregated.
         * - Sending both `startTime`/`endTime` and `fromId` might cause response timeout, please send either `fromId` or `startTime`/`endTime`
         *
         * @summary Compressed/Aggregate Trades List
         * @param {string} symbol Symbol
         * @param {number | bigint} [fromId] ID to get aggregate trades from INCLUSIVE.
         * @param {number | bigint} [startTime] Timestamp in ms to get aggregate trades from INCLUSIVE.
         * @param {number | bigint} [endTime] Timestamp in ms to get aggregate trades until INCLUSIVE.
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        compressedAggregateTradesList: async (
            symbol: string,
            fromId?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('compressedAggregateTradesList', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (fromId !== undefined && fromId !== null) {
                localVarQueryParameter['fromId'] = fromId;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/aggTrades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Kline/candlestick bars for a specific contract type. Klines are uniquely identified by their open time.
         *
         * Weight: based on parameter `LIMIT`
         *
         * | LIMIT | weight |
         * | --- | --- |
         * | [1,100) | 1 |
         * | [100, 500) | 2 |
         * | [500, 1000] | 5 |
         * | > 1000 | 10 |
         *
         * Notes:
         * - The difference between `startTime` and `endTime` can only be up to 200 days
         * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
         * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
         * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
         * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
         *
         * @summary Continuous Contract Kline/Candlestick Data
         * @param {string} pair After CM migration, accepts both CM and UM pair values.
         * @param {ContinuousContractKlineCandlestickDataContractTypeEnum} contractType
         * @param {ContinuousContractKlineCandlestickDataIntervalEnum} interval Interval
         * @param {number | bigint} [startTime] Start time
         * @param {number | bigint} [endTime] End time
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        continuousContractKlineCandlestickData: async (
            pair: string,
            contractType: ContinuousContractKlineCandlestickDataContractTypeEnum,
            interval: ContinuousContractKlineCandlestickDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('continuousContractKlineCandlestickData', 'pair', pair);
            // verify required parameter 'contractType' is not null or undefined
            assertParamExists(
                'continuousContractKlineCandlestickData',
                'contractType',
                contractType
            );
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('continuousContractKlineCandlestickData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/continuousKlines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Current exchange trading rules and symbol information
         *
         * Weight(IP): 1
         *
         * @summary Exchange Information
         *
         * @throws {RequiredError}
         */
        exchangeInformation: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/exchangeInfo',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Funding Rate History of Perpetual Futures
         *
         * Weight(IP): 1
         *
         * Notes:
         * - empty array will be returned for delivery symbols.
         *
         * @summary Get Funding Rate History of Perpetual Futures
         * @param {string} symbol Symbol
         * @param {number | bigint} [startTime] Timestamp in ms to get funding rate from INCLUSIVE.
         * @param {number | bigint} [endTime] Timestamp in ms to get funding rate until INCLUSIVE.
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        getFundingRateHistoryOfPerpetualFutures: async (
            symbol: string,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('getFundingRateHistoryOfPerpetualFutures', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/fundingRate',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query funding rate info for symbols that had FundingRateCap/FundingRateFloor/fundingIntervalHours adjustment
         *
         * @summary Get Funding Rate Info
         *
         * @throws {RequiredError}
         */
        getFundingRateInfo: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/fundingInfo',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query index price and mark price
         *
         * Weight(IP): 10
         *
         * @summary Index Price and Mark Price
         * @param {string} [symbol]
         * @param {string} [pair]
         *
         * @throws {RequiredError}
         */
        indexPriceAndMarkPrice: async (symbol?: string, pair?: string): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/premiumIndex',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Kline/candlestick bars for the index price of a pair. Klines are uniquely identified by their open time.
         *
         * Weight: Based on parameter `LIMIT`
         *
         * | LIMIT | weight |
         * | --- | --- |
         * | [1,100) | 1 |
         * | [100, 500) | 2 |
         * | [500, 1000] | 5 |
         * | > 1000 | 10 |
         *
         * Notes:
         * - The difference between `startTime` and `endTime` can only be up to 200 days
         * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
         * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
         * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
         * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
         *
         * @summary Index Price Kline/Candlestick Data
         * @param {string} pair After CM migration, accepts both CM and UM pair values.
         * @param {IndexPriceKlineCandlestickDataIntervalEnum} interval Interval
         * @param {number | bigint} [startTime] Start time
         * @param {number | bigint} [endTime] End time
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        indexPriceKlineCandlestickData: async (
            pair: string,
            interval: IndexPriceKlineCandlestickDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('indexPriceKlineCandlestickData', 'pair', pair);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('indexPriceKlineCandlestickData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/indexPriceKlines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Kline/candlestick bars for a symbol. Klines are uniquely identified by their open time.
         *
         * Weight: Based on parameter `LIMIT`
         *
         * | LIMIT | weight |
         * | --- | --- |
         * | [1,100) | 1 |
         * | [100, 500) | 2 |
         * | [500, 1000] | 5 |
         * | > 1000 | 10 |
         *
         * Notes:
         * - The difference between `startTime` and `endTime` can only be up to 200 days
         * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
         * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
         * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
         * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
         *
         * @summary Kline/Candlestick Data
         * @param {string} symbol After CM migration, accepts both CM and UM symbols.
         * @param {KlineCandlestickDataIntervalEnum} interval Interval
         * @param {number | bigint} [startTime] Start time
         * @param {number | bigint} [endTime] End time
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        klineCandlestickData: async (
            symbol: string,
            interval: KlineCandlestickDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('klineCandlestickData', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('klineCandlestickData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/klines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query symbol Long/Short Ratio
         *
         * Weight(IP): 1
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Long/Short Ratio
         * @param {string} pair BTCUSD
         * @param {LongShortRatioPeriodEnum} period
         * @param {LongShortRatioContractTypeEnum} [contractType] Contract type filter. If omitted, returns aggregated data across all contract types.
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        longShortRatio: async (
            pair: string,
            period: LongShortRatioPeriodEnum,
            contractType?: LongShortRatioContractTypeEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('longShortRatio', 'pair', pair);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('longShortRatio', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/globalLongShortAccountRatio',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Kline/candlestick bars for the mark price of a symbol. Klines are uniquely identified by their open time.
         *
         * Weight: Based on parameter `LIMIT`
         *
         * | LIMIT | weight |
         * | --- | --- |
         * | [1,100) | 1 |
         * | [100, 500) | 2 |
         * | [500, 1000] | 5 |
         * | > 1000 | 10 |
         *
         * Notes:
         * - The difference between `startTime` and `endTime` can only be up to 200 days
         * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
         * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
         * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
         * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
         *
         * @summary Mark Price Kline/Candlestick Data
         * @param {string} symbol After CM migration, accepts both CM and UM symbols.
         * @param {MarkPriceKlineCandlestickDataIntervalEnum} interval Interval
         * @param {number | bigint} [startTime] Start time
         * @param {number | bigint} [endTime] End time
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        markPriceKlineCandlestickData: async (
            symbol: string,
            interval: MarkPriceKlineCandlestickDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('markPriceKlineCandlestickData', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('markPriceKlineCandlestickData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/markPriceKlines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get older market historical trades.
         *
         * Weight(IP): 200
         *
         * Security Type: MARKET_DATA
         *
         * Notes:
         * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
         * - Only supports data from within the last one month
         *
         * @summary Old Trades Lookup (MARKET_DATA)
         * @param {string} symbol Symbol
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [fromId] TradeId to fetch from. Default gets most recent trades.
         *
         * @throws {RequiredError}
         */
        oldTradesLookup: async (
            symbol: string,
            limit?: number | bigint,
            fromId?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('oldTradesLookup', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (fromId !== undefined && fromId !== null) {
                localVarQueryParameter['fromId'] = fromId;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/historicalTrades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get present open interest of a specific symbol.
         *
         * Weight(IP): 1
         *
         * @summary Open Interest
         * @param {string} symbol Symbol
         *
         * @throws {RequiredError}
         */
        openInterest: async (symbol: string): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('openInterest', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/openInterest',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query open interest stats
         *
         * Weight(IP): 1
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Open Interest Statistics
         * @param {string} pair
         * @param {OpenInterestStatisticsPeriodEnum} period
         * @param {OpenInterestStatisticsContractTypeEnum} [contractType] Contract type filter. If omitted, returns aggregated data across all contract types.
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        openInterestStatistics: async (
            pair: string,
            period: OpenInterestStatisticsPeriodEnum,
            contractType?: OpenInterestStatisticsContractTypeEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('openInterestStatistics', 'pair', pair);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('openInterestStatistics', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/openInterestHist',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query orderbook on specific symbol
         *
         * Weight: Adjusted based on the limit:
         *
         * | Limit | Weight |
         * | ------------ | ------------ |
         * | 5, 10, 20, 50 | 2 |
         * | 100 | 5 |
         * | 500 | 10 |
         * | 1000 | 20 |
         *
         * @summary Order Book
         * @param {string} symbol Symbol
         * @param {number | bigint} [limit] Valid limits:[5, 10, 20, 50, 100, 500, 1000].
         *
         * @throws {RequiredError}
         */
        orderBook: async (symbol: string, limit?: number | bigint): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('orderBook', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/depth',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Premium index kline bars of a symbol. Klines are uniquely identified by their open time.
         *
         * Weight: Based on parameter `LIMIT`
         *
         * | LIMIT       | weight |
         * | ----------- | ------ |
         * | [1,100)     | 1      |
         * | [100, 500)  | 2      |
         * | [500, 1000] | 5      |
         * | > 1000      | 10     |
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent klines are returned.
         *
         * @summary Premium index Kline Data
         * @param {string} symbol After CM migration, accepts both CM and UM symbols.
         * @param {PremiumIndexKlineDataIntervalEnum} interval Interval
         * @param {number | bigint} [startTime] Start time
         * @param {number | bigint} [endTime] End time
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        premiumIndexKlineData: async (
            symbol: string,
            interval: PremiumIndexKlineDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('premiumIndexKlineData', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('premiumIndexKlineData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/premiumIndexKlines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query index price constituents
         *
         * Weight(IP): 1
         *
         * @summary Query Index Price Constituents
         * @param {string} symbol Symbol
         *
         * @throws {RequiredError}
         */
        queryIndexPriceConstituents: async (symbol: string): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('queryIndexPriceConstituents', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/constituents',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get recent market trades
         *
         * Weight(IP): 5
         *
         * Notes:
         * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
         *
         * @summary Recent Trades List
         * @param {string} symbol Symbol
         * @param {number | bigint} [limit] Maximum number of records to return.
         *
         * @throws {RequiredError}
         */
        recentTradesList: async (symbol: string, limit?: number | bigint): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('recentTradesList', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/trades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Best price/qty on the order book for a symbol or symbols.
         *
         * Weight: **2** for a single symbol, **5** when the symbol parameter is omitted
         *
         * Notes:
         * - Symbol and pair cannot be sent together
         * - If a pair is sent,tickers for all symbols of the pair will be returned
         * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
         *
         * @summary Symbol Order Book Ticker
         * @param {string} [symbol] Symbol
         * @param {string} [pair] Symbol
         *
         * @throws {RequiredError}
         */
        symbolOrderBookTicker: async (symbol?: string, pair?: string): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/ticker/bookTicker',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Latest price for a symbol or symbols.
         *
         * Weight: **1** for a single symbol, **2** when the symbol parameter is omitted
         *
         * Notes:
         * - Symbol and pair cannot be sent together
         * - If a pair is sent,tickers for all symbols of the pair will be returned
         * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
         *
         * @summary Symbol Price Ticker
         * @param {string} [symbol] Symbol
         * @param {string} [pair] Pair
         *
         * @throws {RequiredError}
         */
        symbolPriceTicker: async (symbol?: string, pair?: string): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/ticker/price',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Taker Buy Volume: the total volume of buy orders filled by takers within
         * the period.
         *
         * Taker Sell Volume: the total volume of sell orders filled by takers
         * within the period.
         *
         * Weight(IP): 1
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Taker Buy/Sell Volume
         * @param {string} pair
         * @param {TakerBuySellVolumeContractTypeEnum} contractType
         * @param {TakerBuySellVolumePeriodEnum} period
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        takerBuySellVolume: async (
            pair: string,
            contractType: TakerBuySellVolumeContractTypeEnum,
            period: TakerBuySellVolumePeriodEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('takerBuySellVolume', 'pair', pair);
            // verify required parameter 'contractType' is not null or undefined
            assertParamExists('takerBuySellVolume', 'contractType', contractType);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('takerBuySellVolume', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/takerBuySellVol',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Test connectivity to the Rest API.
         *
         * Weight(IP): 1
         *
         * @summary Test Connectivity
         *
         * @throws {RequiredError}
         */
        testConnectivity: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/ping',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * 24 hour rolling window price change statistics.
         *
         * Weight: **1** for a single symbol, **40** when the symbol parameter is omitted
         **Careful** when accessing this with no symbol.
         *
         * Notes:
         * - Symbol and pair cannot be sent together
         * - If a pair is sent,tickers for all symbols of the pair will be returned
         * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
         *
         * @summary 24hr Ticker Price Change Statistics
         * @param {string} [symbol] Symbol
         * @param {string} [pair] Pair
         *
         * @throws {RequiredError}
         */
        ticker24hrPriceChangeStatistics: async (
            symbol?: string,
            pair?: string
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/ticker/24hr',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * The proportion of net long and net short accounts to total accounts of
         * the top 20% users with the highest margin balance. Each account is
         * counted once only.
         *
         * Long Account % = Accounts of top traders with net long positions / Total
         * accounts of top traders with open positions
         *
         * Short Account % = Accounts of top traders with net short positions /
         * Total accounts of top traders with open positions
         *
         * Long/Short Ratio (Accounts) = Long Account % / Short Account %
         *
         * Weight(IP): 1
         *
         * Security Type: Accounts
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Top Trader Long/Short Account Ratio
         * @param {string} pair Pair
         * @param {TopTraderLongShortRatioAccountsPeriodEnum} period
         * @param {TopTraderLongShortRatioAccountsContractTypeEnum} [contractType] Contract type filter. If omitted, returns aggregated data across all contract types.
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        topTraderLongShortRatioAccounts: async (
            pair: string,
            period: TopTraderLongShortRatioAccountsPeriodEnum,
            contractType?: TopTraderLongShortRatioAccountsContractTypeEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('topTraderLongShortRatioAccounts', 'pair', pair);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('topTraderLongShortRatioAccounts', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/topLongShortAccountRatio',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * The proportion of net long and net short positions to total open
         * positions of the top 20% users with the highest margin balance.
         *
         * Long Position % = Long positions of top traders / Total open positions
         * of top traders
         *
         * Short Position % = Short positions of top traders / Total open positions
         * of top traders
         *
         * Long/Short Ratio (Positions) = Long Position % / Short Position %
         *
         * Weight(IP): 1
         *
         * Security Type: Positions
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent data is returned.
         * - Only the data of the latest 30 days is available.
         *
         * @summary Top Trader Long/Short Position Ratio
         * @param {string} pair
         * @param {TopTraderLongShortRatioPositionsPeriodEnum} period
         * @param {TopTraderLongShortRatioPositionsContractTypeEnum} [contractType] Contract type filter. If omitted, returns aggregated data across all contract types.
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         *
         * @throws {RequiredError}
         */
        topTraderLongShortRatioPositions: async (
            pair: string,
            period: TopTraderLongShortRatioPositionsPeriodEnum,
            contractType?: TopTraderLongShortRatioPositionsContractTypeEnum,
            limit?: number | bigint,
            startTime?: number | bigint,
            endTime?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'pair' is not null or undefined
            assertParamExists('topTraderLongShortRatioPositions', 'pair', pair);
            // verify required parameter 'period' is not null or undefined
            assertParamExists('topTraderLongShortRatioPositions', 'period', period);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (period !== undefined && period !== null) {
                localVarQueryParameter['period'] = period;
            }
            if (contractType !== undefined && contractType !== null) {
                localVarQueryParameter['contractType'] = contractType;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/futures/data/topLongShortPositionRatio',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * MarketDataApi - interface
 * @interface MarketDataApi
 */
export interface MarketDataApiInterface {
    /**
     * Query basis
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Basis
     * @param {BasisRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    basis(requestParameters: BasisRequest): Promise<RestApiResponse<BasisResponse>>;
    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * @summary Check Server time
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    checkServerTime(): Promise<RestApiResponse<CheckServerTimeResponse>>;
    /**
     * Get compressed, aggregate trades. Market trades that fill in 100ms with
     * the same price and the same taking side will have the quantity
     * aggregated.
     *
     * Weight(IP): 20
     *
     * Notes:
     * - only trade histories within the past 48 hours (counted from now) can be queried
     * - If both `startTime` and `endTime` are sent, time between `startTime` and `endTime` must be less than 1 hour.
     * - If `fromId`, `startTime`, and `endTime` are not sent, the most recent aggregate trades will be returned.
     * - Only market trades will be aggregated and returned, which means the insurance fund trades and ADL trades won't be aggregated.
     * - Sending both `startTime`/`endTime` and `fromId` might cause response timeout, please send either `fromId` or `startTime`/`endTime`
     *
     * @summary Compressed/Aggregate Trades List
     * @param {CompressedAggregateTradesListRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    compressedAggregateTradesList(
        requestParameters: CompressedAggregateTradesListRequest
    ): Promise<RestApiResponse<CompressedAggregateTradesListResponse>>;
    /**
     * Kline/candlestick bars for a specific contract type. Klines are uniquely identified by their open time.
     *
     * Weight: based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Continuous Contract Kline/Candlestick Data
     * @param {ContinuousContractKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    continuousContractKlineCandlestickData(
        requestParameters: ContinuousContractKlineCandlestickDataRequest
    ): Promise<RestApiResponse<ContinuousContractKlineCandlestickDataResponse>>;
    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 1
     *
     * @summary Exchange Information
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    exchangeInformation(): Promise<RestApiResponse<ExchangeInformationResponse>>;
    /**
     * Get Funding Rate History of Perpetual Futures
     *
     * Weight(IP): 1
     *
     * Notes:
     * - empty array will be returned for delivery symbols.
     *
     * @summary Get Funding Rate History of Perpetual Futures
     * @param {GetFundingRateHistoryOfPerpetualFuturesRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    getFundingRateHistoryOfPerpetualFutures(
        requestParameters: GetFundingRateHistoryOfPerpetualFuturesRequest
    ): Promise<RestApiResponse<GetFundingRateHistoryOfPerpetualFuturesResponse>>;
    /**
     * Query funding rate info for symbols that had FundingRateCap/FundingRateFloor/fundingIntervalHours adjustment
     *
     * @summary Get Funding Rate Info
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    getFundingRateInfo(): Promise<RestApiResponse<GetFundingRateInfoResponse>>;
    /**
     * Query index price and mark price
     *
     * Weight(IP): 10
     *
     * @summary Index Price and Mark Price
     * @param {IndexPriceAndMarkPriceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    indexPriceAndMarkPrice(
        requestParameters?: IndexPriceAndMarkPriceRequest
    ): Promise<RestApiResponse<IndexPriceAndMarkPriceResponse>>;
    /**
     * Kline/candlestick bars for the index price of a pair. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Index Price Kline/Candlestick Data
     * @param {IndexPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    indexPriceKlineCandlestickData(
        requestParameters: IndexPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<IndexPriceKlineCandlestickDataResponse>>;
    /**
     * Kline/candlestick bars for a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Kline/Candlestick Data
     * @param {KlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    klineCandlestickData(
        requestParameters: KlineCandlestickDataRequest
    ): Promise<RestApiResponse<KlineCandlestickDataResponse>>;
    /**
     * Query symbol Long/Short Ratio
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Long/Short Ratio
     * @param {LongShortRatioRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    longShortRatio(
        requestParameters: LongShortRatioRequest
    ): Promise<RestApiResponse<LongShortRatioResponse>>;
    /**
     * Kline/candlestick bars for the mark price of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Mark Price Kline/Candlestick Data
     * @param {MarkPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    markPriceKlineCandlestickData(
        requestParameters: MarkPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<MarkPriceKlineCandlestickDataResponse>>;
    /**
     * Get older market historical trades.
     *
     * Weight(IP): 200
     *
     * Security Type: MARKET_DATA
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     * - Only supports data from within the last one month
     *
     * @summary Old Trades Lookup (MARKET_DATA)
     * @param {OldTradesLookupRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    oldTradesLookup(
        requestParameters: OldTradesLookupRequest
    ): Promise<RestApiResponse<OldTradesLookupResponse>>;
    /**
     * Get present open interest of a specific symbol.
     *
     * Weight(IP): 1
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    openInterest(
        requestParameters: OpenInterestRequest
    ): Promise<RestApiResponse<OpenInterestResponse>>;
    /**
     * Query open interest stats
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Open Interest Statistics
     * @param {OpenInterestStatisticsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    openInterestStatistics(
        requestParameters: OpenInterestStatisticsRequest
    ): Promise<RestApiResponse<OpenInterestStatisticsResponse>>;
    /**
     * Query orderbook on specific symbol
     *
     * Weight: Adjusted based on the limit:
     *
     * | Limit | Weight |
     * | ------------ | ------------ |
     * | 5, 10, 20, 50 | 2 |
     * | 100 | 5 |
     * | 500 | 10 |
     * | 1000 | 20 |
     *
     * @summary Order Book
     * @param {OrderBookRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    orderBook(requestParameters: OrderBookRequest): Promise<RestApiResponse<OrderBookResponse>>;
    /**
     * Premium index kline bars of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT       | weight |
     * | ----------- | ------ |
     * | [1,100)     | 1      |
     * | [100, 500)  | 2      |
     * | [500, 1000] | 5      |
     * | > 1000      | 10     |
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent klines are returned.
     *
     * @summary Premium index Kline Data
     * @param {PremiumIndexKlineDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    premiumIndexKlineData(
        requestParameters: PremiumIndexKlineDataRequest
    ): Promise<RestApiResponse<PremiumIndexKlineDataResponse>>;
    /**
     * Query index price constituents
     *
     * Weight(IP): 1
     *
     * @summary Query Index Price Constituents
     * @param {QueryIndexPriceConstituentsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    queryIndexPriceConstituents(
        requestParameters: QueryIndexPriceConstituentsRequest
    ): Promise<RestApiResponse<QueryIndexPriceConstituentsResponse>>;
    /**
     * Get recent market trades
     *
     * Weight(IP): 5
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     *
     * @summary Recent Trades List
     * @param {RecentTradesListRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    recentTradesList(
        requestParameters: RecentTradesListRequest
    ): Promise<RestApiResponse<RecentTradesListResponse>>;
    /**
     * Best price/qty on the order book for a symbol or symbols.
     *
     * Weight: **2** for a single symbol, **5** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Order Book Ticker
     * @param {SymbolOrderBookTickerRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    symbolOrderBookTicker(
        requestParameters?: SymbolOrderBookTickerRequest
    ): Promise<RestApiResponse<SymbolOrderBookTickerResponse>>;
    /**
     * Latest price for a symbol or symbols.
     *
     * Weight: **1** for a single symbol, **2** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Price Ticker
     * @param {SymbolPriceTickerRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    symbolPriceTicker(
        requestParameters?: SymbolPriceTickerRequest
    ): Promise<RestApiResponse<SymbolPriceTickerResponse>>;
    /**
     * Taker Buy Volume: the total volume of buy orders filled by takers within
     * the period.
     *
     * Taker Sell Volume: the total volume of sell orders filled by takers
     * within the period.
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Taker Buy/Sell Volume
     * @param {TakerBuySellVolumeRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    takerBuySellVolume(
        requestParameters: TakerBuySellVolumeRequest
    ): Promise<RestApiResponse<TakerBuySellVolumeResponse>>;
    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * @summary Test Connectivity
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    testConnectivity(): Promise<RestApiResponse<void>>;
    /**
     * 24 hour rolling window price change statistics.
     *
     * Weight: **1** for a single symbol, **40** when the symbol parameter is omitted
     **Careful** when accessing this with no symbol.
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary 24hr Ticker Price Change Statistics
     * @param {Ticker24hrPriceChangeStatisticsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    ticker24hrPriceChangeStatistics(
        requestParameters?: Ticker24hrPriceChangeStatisticsRequest
    ): Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>>;
    /**
     * The proportion of net long and net short accounts to total accounts of
     * the top 20% users with the highest margin balance. Each account is
     * counted once only.
     *
     * Long Account % = Accounts of top traders with net long positions / Total
     * accounts of top traders with open positions
     *
     * Short Account % = Accounts of top traders with net short positions /
     * Total accounts of top traders with open positions
     *
     * Long/Short Ratio (Accounts) = Long Account % / Short Account %
     *
     * Weight(IP): 1
     *
     * Security Type: Accounts
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Account Ratio
     * @param {TopTraderLongShortRatioAccountsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    topTraderLongShortRatioAccounts(
        requestParameters: TopTraderLongShortRatioAccountsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioAccountsResponse>>;
    /**
     * The proportion of net long and net short positions to total open
     * positions of the top 20% users with the highest margin balance.
     *
     * Long Position % = Long positions of top traders / Total open positions
     * of top traders
     *
     * Short Position % = Short positions of top traders / Total open positions
     * of top traders
     *
     * Long/Short Ratio (Positions) = Long Position % / Short Position %
     *
     * Weight(IP): 1
     *
     * Security Type: Positions
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Position Ratio
     * @param {TopTraderLongShortRatioPositionsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    topTraderLongShortRatioPositions(
        requestParameters: TopTraderLongShortRatioPositionsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioPositionsResponse>>;
}

/**
 * Request parameters for basis operation in MarketDataApi.
 * @interface BasisRequest
 */
export interface BasisRequest {
    /**
     * Pair.
     * @type {string}
     * @memberof MarketDataApiBasis
     */
    readonly pair: string;

    /**
     * Contract type.
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiBasis
     */
    readonly contractType: BasisContractTypeEnum;

    /**
     * Period interval.
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiBasis
     */
    readonly period: BasisPeriodEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiBasis
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiBasis
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiBasis
     */
    readonly endTime?: number | bigint;
}

/**
 * Request parameters for compressedAggregateTradesList operation in MarketDataApi.
 * @interface CompressedAggregateTradesListRequest
 */
export interface CompressedAggregateTradesListRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiCompressedAggregateTradesList
     */
    readonly symbol: string;

    /**
     * ID to get aggregate trades from INCLUSIVE.
     * @type {number | bigint}
     * @memberof MarketDataApiCompressedAggregateTradesList
     */
    readonly fromId?: number | bigint;

    /**
     * Timestamp in ms to get aggregate trades from INCLUSIVE.
     * @type {number | bigint}
     * @memberof MarketDataApiCompressedAggregateTradesList
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms to get aggregate trades until INCLUSIVE.
     * @type {number | bigint}
     * @memberof MarketDataApiCompressedAggregateTradesList
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiCompressedAggregateTradesList
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for continuousContractKlineCandlestickData operation in MarketDataApi.
 * @interface ContinuousContractKlineCandlestickDataRequest
 */
export interface ContinuousContractKlineCandlestickDataRequest {
    /**
     * After CM migration, accepts both CM and UM pair values.
     * @type {string}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly pair: string;

    /**
     *
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly contractType: ContinuousContractKlineCandlestickDataContractTypeEnum;

    /**
     * Interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly interval: ContinuousContractKlineCandlestickDataIntervalEnum;

    /**
     * Start time
     * @type {number | bigint}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly startTime?: number | bigint;

    /**
     * End time
     * @type {number | bigint}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiContinuousContractKlineCandlestickData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for getFundingRateHistoryOfPerpetualFutures operation in MarketDataApi.
 * @interface GetFundingRateHistoryOfPerpetualFuturesRequest
 */
export interface GetFundingRateHistoryOfPerpetualFuturesRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiGetFundingRateHistoryOfPerpetualFutures
     */
    readonly symbol: string;

    /**
     * Timestamp in ms to get funding rate from INCLUSIVE.
     * @type {number | bigint}
     * @memberof MarketDataApiGetFundingRateHistoryOfPerpetualFutures
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms to get funding rate until INCLUSIVE.
     * @type {number | bigint}
     * @memberof MarketDataApiGetFundingRateHistoryOfPerpetualFutures
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiGetFundingRateHistoryOfPerpetualFutures
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for indexPriceAndMarkPrice operation in MarketDataApi.
 * @interface IndexPriceAndMarkPriceRequest
 */
export interface IndexPriceAndMarkPriceRequest {
    /**
     *
     * @type {string}
     * @memberof MarketDataApiIndexPriceAndMarkPrice
     */
    readonly symbol?: string;

    /**
     *
     * @type {string}
     * @memberof MarketDataApiIndexPriceAndMarkPrice
     */
    readonly pair?: string;
}

/**
 * Request parameters for indexPriceKlineCandlestickData operation in MarketDataApi.
 * @interface IndexPriceKlineCandlestickDataRequest
 */
export interface IndexPriceKlineCandlestickDataRequest {
    /**
     * After CM migration, accepts both CM and UM pair values.
     * @type {string}
     * @memberof MarketDataApiIndexPriceKlineCandlestickData
     */
    readonly pair: string;

    /**
     * Interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiIndexPriceKlineCandlestickData
     */
    readonly interval: IndexPriceKlineCandlestickDataIntervalEnum;

    /**
     * Start time
     * @type {number | bigint}
     * @memberof MarketDataApiIndexPriceKlineCandlestickData
     */
    readonly startTime?: number | bigint;

    /**
     * End time
     * @type {number | bigint}
     * @memberof MarketDataApiIndexPriceKlineCandlestickData
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiIndexPriceKlineCandlestickData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for klineCandlestickData operation in MarketDataApi.
 * @interface KlineCandlestickDataRequest
 */
export interface KlineCandlestickDataRequest {
    /**
     * After CM migration, accepts both CM and UM symbols.
     * @type {string}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly symbol: string;

    /**
     * Interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly interval: KlineCandlestickDataIntervalEnum;

    /**
     * Start time
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly startTime?: number | bigint;

    /**
     * End time
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for longShortRatio operation in MarketDataApi.
 * @interface LongShortRatioRequest
 */
export interface LongShortRatioRequest {
    /**
     * BTCUSD
     * @type {string}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly pair: string;

    /**
     *
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly period: LongShortRatioPeriodEnum;

    /**
     * Contract type filter. If omitted, returns aggregated data across all contract types.
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly contractType?: LongShortRatioContractTypeEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiLongShortRatio
     */
    readonly endTime?: number | bigint;
}

/**
 * Request parameters for markPriceKlineCandlestickData operation in MarketDataApi.
 * @interface MarkPriceKlineCandlestickDataRequest
 */
export interface MarkPriceKlineCandlestickDataRequest {
    /**
     * After CM migration, accepts both CM and UM symbols.
     * @type {string}
     * @memberof MarketDataApiMarkPriceKlineCandlestickData
     */
    readonly symbol: string;

    /**
     * Interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiMarkPriceKlineCandlestickData
     */
    readonly interval: MarkPriceKlineCandlestickDataIntervalEnum;

    /**
     * Start time
     * @type {number | bigint}
     * @memberof MarketDataApiMarkPriceKlineCandlestickData
     */
    readonly startTime?: number | bigint;

    /**
     * End time
     * @type {number | bigint}
     * @memberof MarketDataApiMarkPriceKlineCandlestickData
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiMarkPriceKlineCandlestickData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for oldTradesLookup operation in MarketDataApi.
 * @interface OldTradesLookupRequest
 */
export interface OldTradesLookupRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiOldTradesLookup
     */
    readonly symbol: string;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiOldTradesLookup
     */
    readonly limit?: number | bigint;

    /**
     * TradeId to fetch from. Default gets most recent trades.
     * @type {number | bigint}
     * @memberof MarketDataApiOldTradesLookup
     */
    readonly fromId?: number | bigint;
}

/**
 * Request parameters for openInterest operation in MarketDataApi.
 * @interface OpenInterestRequest
 */
export interface OpenInterestRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiOpenInterest
     */
    readonly symbol: string;
}

/**
 * Request parameters for openInterestStatistics operation in MarketDataApi.
 * @interface OpenInterestStatisticsRequest
 */
export interface OpenInterestStatisticsRequest {
    /**
     *
     * @type {string}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly pair: string;

    /**
     *
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly period: OpenInterestStatisticsPeriodEnum;

    /**
     * Contract type filter. If omitted, returns aggregated data across all contract types.
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly contractType?: OpenInterestStatisticsContractTypeEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiOpenInterestStatistics
     */
    readonly endTime?: number | bigint;
}

/**
 * Request parameters for orderBook operation in MarketDataApi.
 * @interface OrderBookRequest
 */
export interface OrderBookRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiOrderBook
     */
    readonly symbol: string;

    /**
     * Valid limits:[5, 10, 20, 50, 100, 500, 1000].
     * @type {number | bigint}
     * @memberof MarketDataApiOrderBook
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for premiumIndexKlineData operation in MarketDataApi.
 * @interface PremiumIndexKlineDataRequest
 */
export interface PremiumIndexKlineDataRequest {
    /**
     * After CM migration, accepts both CM and UM symbols.
     * @type {string}
     * @memberof MarketDataApiPremiumIndexKlineData
     */
    readonly symbol: string;

    /**
     * Interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiPremiumIndexKlineData
     */
    readonly interval: PremiumIndexKlineDataIntervalEnum;

    /**
     * Start time
     * @type {number | bigint}
     * @memberof MarketDataApiPremiumIndexKlineData
     */
    readonly startTime?: number | bigint;

    /**
     * End time
     * @type {number | bigint}
     * @memberof MarketDataApiPremiumIndexKlineData
     */
    readonly endTime?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiPremiumIndexKlineData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for queryIndexPriceConstituents operation in MarketDataApi.
 * @interface QueryIndexPriceConstituentsRequest
 */
export interface QueryIndexPriceConstituentsRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiQueryIndexPriceConstituents
     */
    readonly symbol: string;
}

/**
 * Request parameters for recentTradesList operation in MarketDataApi.
 * @interface RecentTradesListRequest
 */
export interface RecentTradesListRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiRecentTradesList
     */
    readonly symbol: string;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiRecentTradesList
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for symbolOrderBookTicker operation in MarketDataApi.
 * @interface SymbolOrderBookTickerRequest
 */
export interface SymbolOrderBookTickerRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiSymbolOrderBookTicker
     */
    readonly symbol?: string;

    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiSymbolOrderBookTicker
     */
    readonly pair?: string;
}

/**
 * Request parameters for symbolPriceTicker operation in MarketDataApi.
 * @interface SymbolPriceTickerRequest
 */
export interface SymbolPriceTickerRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiSymbolPriceTicker
     */
    readonly symbol?: string;

    /**
     * Pair
     * @type {string}
     * @memberof MarketDataApiSymbolPriceTicker
     */
    readonly pair?: string;
}

/**
 * Request parameters for takerBuySellVolume operation in MarketDataApi.
 * @interface TakerBuySellVolumeRequest
 */
export interface TakerBuySellVolumeRequest {
    /**
     *
     * @type {string}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly pair: string;

    /**
     *
     * @type {'ALL' | 'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly contractType: TakerBuySellVolumeContractTypeEnum;

    /**
     *
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly period: TakerBuySellVolumePeriodEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTakerBuySellVolume
     */
    readonly endTime?: number | bigint;
}

/**
 * Request parameters for ticker24hrPriceChangeStatistics operation in MarketDataApi.
 * @interface Ticker24hrPriceChangeStatisticsRequest
 */
export interface Ticker24hrPriceChangeStatisticsRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof MarketDataApiTicker24hrPriceChangeStatistics
     */
    readonly symbol?: string;

    /**
     * Pair
     * @type {string}
     * @memberof MarketDataApiTicker24hrPriceChangeStatistics
     */
    readonly pair?: string;
}

/**
 * Request parameters for topTraderLongShortRatioAccounts operation in MarketDataApi.
 * @interface TopTraderLongShortRatioAccountsRequest
 */
export interface TopTraderLongShortRatioAccountsRequest {
    /**
     * Pair
     * @type {string}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly pair: string;

    /**
     *
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly period: TopTraderLongShortRatioAccountsPeriodEnum;

    /**
     * Contract type filter. If omitted, returns aggregated data across all contract types.
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly contractType?: TopTraderLongShortRatioAccountsContractTypeEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioAccounts
     */
    readonly endTime?: number | bigint;
}

/**
 * Request parameters for topTraderLongShortRatioPositions operation in MarketDataApi.
 * @interface TopTraderLongShortRatioPositionsRequest
 */
export interface TopTraderLongShortRatioPositionsRequest {
    /**
     *
     * @type {string}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly pair: string;

    /**
     *
     * @type {'5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly period: TopTraderLongShortRatioPositionsPeriodEnum;

    /**
     * Contract type filter. If omitted, returns aggregated data across all contract types.
     * @type {'PERPETUAL' | 'CURRENT_QUARTER' | 'NEXT_QUARTER'}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly contractType?: TopTraderLongShortRatioPositionsContractTypeEnum;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof MarketDataApiTopTraderLongShortRatioPositions
     */
    readonly endTime?: number | bigint;
}

/**
 * MarketDataApi - object-oriented interface
 * @class MarketDataApi
 */
export class MarketDataApi implements MarketDataApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = MarketDataApiAxiosParamCreator(configuration);
    }

    /**
     * Query basis
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Basis
     * @param {BasisRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<BasisResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#basis Binance API Documentation}
     */
    public async basis(requestParameters: BasisRequest): Promise<RestApiResponse<BasisResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.basis(
            requestParameters?.pair,
            requestParameters?.contractType,
            requestParameters?.period,
            requestParameters?.limit,
            requestParameters?.startTime,
            requestParameters?.endTime
        );
        return sendRequest<BasisResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * @summary Check Server time
     * @returns {Promise<RestApiResponse<CheckServerTimeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#check-server-time Binance API Documentation}
     */
    public async checkServerTime(): Promise<RestApiResponse<CheckServerTimeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.checkServerTime();
        return sendRequest<CheckServerTimeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get compressed, aggregate trades. Market trades that fill in 100ms with
     * the same price and the same taking side will have the quantity
     * aggregated.
     *
     * Weight(IP): 20
     *
     * Notes:
     * - only trade histories within the past 48 hours (counted from now) can be queried
     * - If both `startTime` and `endTime` are sent, time between `startTime` and `endTime` must be less than 1 hour.
     * - If `fromId`, `startTime`, and `endTime` are not sent, the most recent aggregate trades will be returned.
     * - Only market trades will be aggregated and returned, which means the insurance fund trades and ADL trades won't be aggregated.
     * - Sending both `startTime`/`endTime` and `fromId` might cause response timeout, please send either `fromId` or `startTime`/`endTime`
     *
     * @summary Compressed/Aggregate Trades List
     * @param {CompressedAggregateTradesListRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<CompressedAggregateTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#compressed-aggregate-trades-list Binance API Documentation}
     */
    public async compressedAggregateTradesList(
        requestParameters: CompressedAggregateTradesListRequest
    ): Promise<RestApiResponse<CompressedAggregateTradesListResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.compressedAggregateTradesList(
                requestParameters?.symbol,
                requestParameters?.fromId,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.limit
            );
        return sendRequest<CompressedAggregateTradesListResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Kline/candlestick bars for a specific contract type. Klines are uniquely identified by their open time.
     *
     * Weight: based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Continuous Contract Kline/Candlestick Data
     * @param {ContinuousContractKlineCandlestickDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<ContinuousContractKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#continuous-contract-kline-candlestick-data Binance API Documentation}
     */
    public async continuousContractKlineCandlestickData(
        requestParameters: ContinuousContractKlineCandlestickDataRequest
    ): Promise<RestApiResponse<ContinuousContractKlineCandlestickDataResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.continuousContractKlineCandlestickData(
                requestParameters?.pair,
                requestParameters?.contractType,
                requestParameters?.interval,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.limit
            );
        return sendRequest<ContinuousContractKlineCandlestickDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 1
     *
     * @summary Exchange Information
     * @returns {Promise<RestApiResponse<ExchangeInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#exchange-information Binance API Documentation}
     */
    public async exchangeInformation(): Promise<RestApiResponse<ExchangeInformationResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.exchangeInformation();
        return sendRequest<ExchangeInformationResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get Funding Rate History of Perpetual Futures
     *
     * Weight(IP): 1
     *
     * Notes:
     * - empty array will be returned for delivery symbols.
     *
     * @summary Get Funding Rate History of Perpetual Futures
     * @param {GetFundingRateHistoryOfPerpetualFuturesRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFundingRateHistoryOfPerpetualFuturesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#get-funding-rate-history-of-perpetual-futures Binance API Documentation}
     */
    public async getFundingRateHistoryOfPerpetualFutures(
        requestParameters: GetFundingRateHistoryOfPerpetualFuturesRequest
    ): Promise<RestApiResponse<GetFundingRateHistoryOfPerpetualFuturesResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getFundingRateHistoryOfPerpetualFutures(
                requestParameters?.symbol,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.limit
            );
        return sendRequest<GetFundingRateHistoryOfPerpetualFuturesResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query funding rate info for symbols that had FundingRateCap/FundingRateFloor/fundingIntervalHours adjustment
     *
     * @summary Get Funding Rate Info
     * @returns {Promise<RestApiResponse<GetFundingRateInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#get-funding-rate-info Binance API Documentation}
     */
    public async getFundingRateInfo(): Promise<RestApiResponse<GetFundingRateInfoResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getFundingRateInfo();
        return sendRequest<GetFundingRateInfoResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query index price and mark price
     *
     * Weight(IP): 10
     *
     * @summary Index Price and Mark Price
     * @param {IndexPriceAndMarkPriceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<IndexPriceAndMarkPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#index-price-and-mark-price Binance API Documentation}
     */
    public async indexPriceAndMarkPrice(
        requestParameters: IndexPriceAndMarkPriceRequest = {}
    ): Promise<RestApiResponse<IndexPriceAndMarkPriceResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.indexPriceAndMarkPrice(
            requestParameters?.symbol,
            requestParameters?.pair
        );
        return sendRequest<IndexPriceAndMarkPriceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Kline/candlestick bars for the index price of a pair. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Index Price Kline/Candlestick Data
     * @param {IndexPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<IndexPriceKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#index-price-kline-candlestick-data Binance API Documentation}
     */
    public async indexPriceKlineCandlestickData(
        requestParameters: IndexPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<IndexPriceKlineCandlestickDataResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.indexPriceKlineCandlestickData(
                requestParameters?.pair,
                requestParameters?.interval,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.limit
            );
        return sendRequest<IndexPriceKlineCandlestickDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Kline/candlestick bars for a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Kline/Candlestick Data
     * @param {KlineCandlestickDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<KlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#kline-candlestick-data Binance API Documentation}
     */
    public async klineCandlestickData(
        requestParameters: KlineCandlestickDataRequest
    ): Promise<RestApiResponse<KlineCandlestickDataResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.klineCandlestickData(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit
        );
        return sendRequest<KlineCandlestickDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query symbol Long/Short Ratio
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Long/Short Ratio
     * @param {LongShortRatioRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<LongShortRatioResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#long-short-ratio Binance API Documentation}
     */
    public async longShortRatio(
        requestParameters: LongShortRatioRequest
    ): Promise<RestApiResponse<LongShortRatioResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.longShortRatio(
            requestParameters?.pair,
            requestParameters?.period,
            requestParameters?.contractType,
            requestParameters?.limit,
            requestParameters?.startTime,
            requestParameters?.endTime
        );
        return sendRequest<LongShortRatioResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Kline/candlestick bars for the mark price of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Mark Price Kline/Candlestick Data
     * @param {MarkPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<MarkPriceKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#mark-price-kline-candlestick-data Binance API Documentation}
     */
    public async markPriceKlineCandlestickData(
        requestParameters: MarkPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<MarkPriceKlineCandlestickDataResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.markPriceKlineCandlestickData(
                requestParameters?.symbol,
                requestParameters?.interval,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.limit
            );
        return sendRequest<MarkPriceKlineCandlestickDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get older market historical trades.
     *
     * Weight(IP): 200
     *
     * Security Type: MARKET_DATA
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     * - Only supports data from within the last one month
     *
     * @summary Old Trades Lookup (MARKET_DATA)
     * @param {OldTradesLookupRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OldTradesLookupResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#old-trades-lookup Binance API Documentation}
     */
    public async oldTradesLookup(
        requestParameters: OldTradesLookupRequest
    ): Promise<RestApiResponse<OldTradesLookupResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.oldTradesLookup(
            requestParameters?.symbol,
            requestParameters?.limit,
            requestParameters?.fromId
        );
        return sendRequest<OldTradesLookupResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get present open interest of a specific symbol.
     *
     * Weight(IP): 1
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OpenInterestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#open-interest Binance API Documentation}
     */
    public async openInterest(
        requestParameters: OpenInterestRequest
    ): Promise<RestApiResponse<OpenInterestResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.openInterest(
            requestParameters?.symbol
        );
        return sendRequest<OpenInterestResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query open interest stats
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Open Interest Statistics
     * @param {OpenInterestStatisticsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OpenInterestStatisticsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#open-interest-statistics Binance API Documentation}
     */
    public async openInterestStatistics(
        requestParameters: OpenInterestStatisticsRequest
    ): Promise<RestApiResponse<OpenInterestStatisticsResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.openInterestStatistics(
            requestParameters?.pair,
            requestParameters?.period,
            requestParameters?.contractType,
            requestParameters?.limit,
            requestParameters?.startTime,
            requestParameters?.endTime
        );
        return sendRequest<OpenInterestStatisticsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query orderbook on specific symbol
     *
     * Weight: Adjusted based on the limit:
     *
     * | Limit | Weight |
     * | ------------ | ------------ |
     * | 5, 10, 20, 50 | 2 |
     * | 100 | 5 |
     * | 500 | 10 |
     * | 1000 | 20 |
     *
     * @summary Order Book
     * @param {OrderBookRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OrderBookResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#order-book Binance API Documentation}
     */
    public async orderBook(
        requestParameters: OrderBookRequest
    ): Promise<RestApiResponse<OrderBookResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.orderBook(
            requestParameters?.symbol,
            requestParameters?.limit
        );
        return sendRequest<OrderBookResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Premium index kline bars of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT       | weight |
     * | ----------- | ------ |
     * | [1,100)     | 1      |
     * | [100, 500)  | 2      |
     * | [500, 1000] | 5      |
     * | > 1000      | 10     |
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent klines are returned.
     *
     * @summary Premium index Kline Data
     * @param {PremiumIndexKlineDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<PremiumIndexKlineDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#premium-index-kline-data Binance API Documentation}
     */
    public async premiumIndexKlineData(
        requestParameters: PremiumIndexKlineDataRequest
    ): Promise<RestApiResponse<PremiumIndexKlineDataResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.premiumIndexKlineData(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit
        );
        return sendRequest<PremiumIndexKlineDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query index price constituents
     *
     * Weight(IP): 1
     *
     * @summary Query Index Price Constituents
     * @param {QueryIndexPriceConstituentsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryIndexPriceConstituentsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#query-index-price-constituents Binance API Documentation}
     */
    public async queryIndexPriceConstituents(
        requestParameters: QueryIndexPriceConstituentsRequest
    ): Promise<RestApiResponse<QueryIndexPriceConstituentsResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.queryIndexPriceConstituents(
            requestParameters?.symbol
        );
        return sendRequest<QueryIndexPriceConstituentsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get recent market trades
     *
     * Weight(IP): 5
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     *
     * @summary Recent Trades List
     * @param {RecentTradesListRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<RecentTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#recent-trades-list Binance API Documentation}
     */
    public async recentTradesList(
        requestParameters: RecentTradesListRequest
    ): Promise<RestApiResponse<RecentTradesListResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.recentTradesList(
            requestParameters?.symbol,
            requestParameters?.limit
        );
        return sendRequest<RecentTradesListResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Best price/qty on the order book for a symbol or symbols.
     *
     * Weight: **2** for a single symbol, **5** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Order Book Ticker
     * @param {SymbolOrderBookTickerRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SymbolOrderBookTickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#symbol-order-book-ticker Binance API Documentation}
     */
    public async symbolOrderBookTicker(
        requestParameters: SymbolOrderBookTickerRequest = {}
    ): Promise<RestApiResponse<SymbolOrderBookTickerResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.symbolOrderBookTicker(
            requestParameters?.symbol,
            requestParameters?.pair
        );
        return sendRequest<SymbolOrderBookTickerResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Latest price for a symbol or symbols.
     *
     * Weight: **1** for a single symbol, **2** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Price Ticker
     * @param {SymbolPriceTickerRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SymbolPriceTickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#symbol-price-ticker Binance API Documentation}
     */
    public async symbolPriceTicker(
        requestParameters: SymbolPriceTickerRequest = {}
    ): Promise<RestApiResponse<SymbolPriceTickerResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.symbolPriceTicker(
            requestParameters?.symbol,
            requestParameters?.pair
        );
        return sendRequest<SymbolPriceTickerResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Taker Buy Volume: the total volume of buy orders filled by takers within
     * the period.
     *
     * Taker Sell Volume: the total volume of sell orders filled by takers
     * within the period.
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Taker Buy/Sell Volume
     * @param {TakerBuySellVolumeRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<TakerBuySellVolumeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#taker-buy-sell-volume Binance API Documentation}
     */
    public async takerBuySellVolume(
        requestParameters: TakerBuySellVolumeRequest
    ): Promise<RestApiResponse<TakerBuySellVolumeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.takerBuySellVolume(
            requestParameters?.pair,
            requestParameters?.contractType,
            requestParameters?.period,
            requestParameters?.limit,
            requestParameters?.startTime,
            requestParameters?.endTime
        );
        return sendRequest<TakerBuySellVolumeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * @summary Test Connectivity
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#test-connectivity Binance API Documentation}
     */
    public async testConnectivity(): Promise<RestApiResponse<void>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.testConnectivity();
        return sendRequest<void>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * 24 hour rolling window price change statistics.
     *
     * Weight: **1** for a single symbol, **40** when the symbol parameter is omitted
     **Careful** when accessing this with no symbol.
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary 24hr Ticker Price Change Statistics
     * @param {Ticker24hrPriceChangeStatisticsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#ticker24hr-price-change-statistics Binance API Documentation}
     */
    public async ticker24hrPriceChangeStatistics(
        requestParameters: Ticker24hrPriceChangeStatisticsRequest = {}
    ): Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.ticker24hrPriceChangeStatistics(
                requestParameters?.symbol,
                requestParameters?.pair
            );
        return sendRequest<Ticker24hrPriceChangeStatisticsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * The proportion of net long and net short accounts to total accounts of
     * the top 20% users with the highest margin balance. Each account is
     * counted once only.
     *
     * Long Account % = Accounts of top traders with net long positions / Total
     * accounts of top traders with open positions
     *
     * Short Account % = Accounts of top traders with net short positions /
     * Total accounts of top traders with open positions
     *
     * Long/Short Ratio (Accounts) = Long Account % / Short Account %
     *
     * Weight(IP): 1
     *
     * Security Type: Accounts
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Account Ratio
     * @param {TopTraderLongShortRatioAccountsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<TopTraderLongShortRatioAccountsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#top-trader-long-short-ratio-accounts Binance API Documentation}
     */
    public async topTraderLongShortRatioAccounts(
        requestParameters: TopTraderLongShortRatioAccountsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioAccountsResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.topTraderLongShortRatioAccounts(
                requestParameters?.pair,
                requestParameters?.period,
                requestParameters?.contractType,
                requestParameters?.limit,
                requestParameters?.startTime,
                requestParameters?.endTime
            );
        return sendRequest<TopTraderLongShortRatioAccountsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * The proportion of net long and net short positions to total open
     * positions of the top 20% users with the highest margin balance.
     *
     * Long Position % = Long positions of top traders / Total open positions
     * of top traders
     *
     * Short Position % = Short positions of top traders / Total open positions
     * of top traders
     *
     * Long/Short Ratio (Positions) = Long Position % / Short Position %
     *
     * Weight(IP): 1
     *
     * Security Type: Positions
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Position Ratio
     * @param {TopTraderLongShortRatioPositionsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<TopTraderLongShortRatioPositionsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#top-trader-long-short-ratio-positions Binance API Documentation}
     */
    public async topTraderLongShortRatioPositions(
        requestParameters: TopTraderLongShortRatioPositionsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioPositionsResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.topTraderLongShortRatioPositions(
                requestParameters?.pair,
                requestParameters?.period,
                requestParameters?.contractType,
                requestParameters?.limit,
                requestParameters?.startTime,
                requestParameters?.endTime
            );
        return sendRequest<TopTraderLongShortRatioPositionsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }
}

export enum BasisContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum BasisPeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum ContinuousContractKlineCandlestickDataContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum ContinuousContractKlineCandlestickDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum IndexPriceKlineCandlestickDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum KlineCandlestickDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum LongShortRatioPeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum LongShortRatioContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum MarkPriceKlineCandlestickDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum OpenInterestStatisticsPeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum OpenInterestStatisticsContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum PremiumIndexKlineDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum TakerBuySellVolumeContractTypeEnum {
    ALL = 'ALL',
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum TakerBuySellVolumePeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum TopTraderLongShortRatioAccountsPeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum TopTraderLongShortRatioAccountsContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}

export enum TopTraderLongShortRatioPositionsPeriodEnum {
    PERIOD_5m = '5m',
    PERIOD_15m = '15m',
    PERIOD_30m = '30m',
    PERIOD_1h = '1h',
    PERIOD_2h = '2h',
    PERIOD_4h = '4h',
    PERIOD_6h = '6h',
    PERIOD_12h = '12h',
    PERIOD_1d = '1d',
}

export enum TopTraderLongShortRatioPositionsContractTypeEnum {
    PERPETUAL = 'PERPETUAL',
    CURRENT_QUARTER = 'CURRENT_QUARTER',
    NEXT_QUARTER = 'NEXT_QUARTER',
}
